import React, { useState } from 'react';
import { CarePack, Link } from '@hpi-sox/hpstellar-core';
import loadable from '@loadable/component';

import withPageComponents from '../../../page/components/with-page-components';
import withConfigurations from '../../../product/components/product-configurations';
import useProductPrice from '../../../hooks/useProductPrice';
import CompareCarePacks from './compare-carepacks';
import useTestFlags from '../../../abtest/useTestFlags';
import { Helpers } from '../../../core/src/helpers';
import { getDisclaimerMessage } from './utils';

const CarePackAbTest = loadable(() => Helpers.retryFunc(() => import('./carepacks-abtest')));

import './carepack.less';

const getCarePackDuration = name => {
    try {
        return (name.match(/\d[\-\s]?[\yr\year]/gi) || [''])[0].replace(/\D/g, '');
    } catch (e) {}
};

const hasAccidentalDamageCoverage = name => {
    try {
        return name?.toLowerCase().indexOf('accidental damage') > -1;
    } catch (e) {}
};

const getQuantityAdjustedPrice = (price, quantity = 1) => {
    if (!price) {
        return {};
    }
    const { salePrice, regularPrice, priceDifference } = price;
    return {
        ...price,
        regularPrice: regularPrice * quantity,
        salePrice: salePrice * quantity,
        priceDifference: priceDifference * quantity,
        unitPrice: quantity > 1 ? salePrice : null,
    };
};

const getConfigurations = (carepacks, defaultCarePack, prices, primaryComponent) => {
    let recommended = 'HP recommends';
    const carepackBundleQuantity = (primaryComponent?.quantity || 1) * 1;
    const carePackItems = carepacks.reduce(
        (r, c, i) => {
            const { sku, violatorMsg, viewDetails, viewDetailsText = 'View Details', name, ...rest } = c;
            const cpPrice = prices[sku];
            const { salePriceSuffix, ...price } = primaryComponent
                ? getQuantityAdjustedPrice(cpPrice, carepackBundleQuantity)
                : cpPrice || {};

            const isRecommended = !!violatorMsg;
            if (isRecommended) {
                recommended = violatorMsg;
            }
            const label = viewDetails ? (
                <div>
                    {c.name}
                    <br />
                    <Link size="small" to={viewDetails} variation="body" weight="light" target="_blank">
                        {viewDetailsText}
                    </Link>
                </div>
            ) : c.label ? (
                c.label
            ) : (
                c.name
            );
            r.push({
                label,
                isRecommended,
                price: cpPrice ? { ...price, translations: { salePriceSuffix } } : undefined,
                sku: c.sku,
                gtmValue: c.gtmValue,
                accidentalDamageCoverage: hasAccidentalDamageCoverage(name),
                duration: getCarePackDuration(name),
                ...rest,
            });
            return r;
        },
        defaultCarePack
            ? [
                  {
                      label: defaultCarePack.label || defaultCarePack.name,
                      duration: getCarePackDuration(defaultCarePack.name),
                      accidentalDamageCoverage: hasAccidentalDamageCoverage(defaultCarePack.name),
                      isIncluded: true,
                      sku: 'default',
                  },
              ]
            : [],
    );
    return { recommended, carePackItems };
};

const onChange = (sku, setAddons, carepacks, value, qty = 1, options = {}) => {
    const selectCarePack = carepacks.find(c => c.sku === value);
    setAddons(sku, selectCarePack ? [{ product: { ...selectCarePack, ...options }, qty }] : []);
};

const getDerivedTitle = productInitial => {
    const { family } = productInitial || {};
    const normalizedFamily = family?.toLowerCase();
    const device =
        normalizedFamily?.indexOf('desktop') > -1
            ? 'desktop'
            : normalizedFamily?.indexOf('laptop') > -1
              ? 'laptop'
              : normalizedFamily?.indexOf('monitor') > -1
                ? 'monitor'
                : 'device';

    return `Add an HP Care Pack to protect your new ${device}`;
};

const EMPTY_OBJ = {};
const carepacks = ({
    productInitial,
    pdpSettings,
    pdpCarePackBundle,
    addOns,
    setAddOns,
    trackCustomMetric,
    device,
    showFor = ['desktop'],
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);

    const { ctoCarePackEnabled } = useTestFlags(['ctoCarePack']);
    const {
        carepacks: standardCarePacks,
        label,
        defaultCarePack: standardDefault,
        gtmActions,
        serviceBundle,
    } = pdpCarePackBundle || EMPTY_OBJ;
    const { product_type, sku, isSMB, eol, bundlePrimarySKU, bundleComponents } = productInitial || EMPTY_OBJ;
    const {
        carepacks: serviceBundleCarePacks,
        defaultCarePack: serviceBUndleDefault,
        translations: serviceBundleTranslations,
    } = serviceBundle || EMPTY_OBJ;
    const primaryComponent = (bundleComponents || []).find(c => c.catentryId === bundlePrimarySKU);
    const isServiceBundle = serviceBundleCarePacks && serviceBundleCarePacks.length > 0;
    const translations = isServiceBundle ? serviceBundleTranslations : {};
    const defaultCarePack = isServiceBundle ? serviceBUndleDefault : standardDefault;
    const carepacks = isServiceBundle ? serviceBundleCarePacks : standardCarePacks;
    const hasCarePack = carepacks && carepacks.length > 0;
    const { prices } = useProductPrice(carepacks, { returnEmptyPriceObj: false });

    if (!hasCarePack || (product_type === 'CTO' && !ctoCarePackEnabled) || !showFor.includes(device) || eol) {
        return null;
    }
    const addOnCarepacks = addOns[sku];
    const selectedCarepack = (addOnCarepacks && addOnCarepacks[0] && addOnCarepacks[0].product) || defaultCarePack;

    const { recommended, carePackItems } = getConfigurations(carepacks, defaultCarePack, prices, primaryComponent);
    const isSmbNoCarepack = isSMB && !hasCarePack;

    const onChangeSelection = e => {
        const { sku } = productInitial || {};
        let options = {};
        const carepackBundleQuantity = bundlePrimarySKU && primaryComponent ? (primaryComponent.quantity || 1) * 1 : 1;
        if (bundlePrimarySKU && bundleComponents && bundleComponents.length > 0) {
            if (primaryComponent) {
                options = {
                    carepackBundleId: sku,
                    eligibleSKU: primaryComponent.sku,
                    carepackBundleQuantity,
                };
            }
        }
        onChange(sku, setAddOns, carepacks, e?.target?.value, carepackBundleQuantity, options);
        setIsModalOpen(false);
        // TODO: remove once stellar has a fix for their modal close
        document.body.style = null;
    };

    const modalClose = () => setIsModalOpen(false);
    const {
        disclaimer: disclaimerText = 'Service starts on date of hardware purchase. Restrictions and limitations apply.',
    } = translations;
    const disclaimer = getDisclaimerMessage(disclaimerText, primaryComponent);
    return (
        <div className="pdp-carepack-module">
            <CarePackAbTest
                detailsClick={
                    isServiceBundle
                        ? () => {
                              setIsModalOpen(true);
                          }
                        : null
                }
                items={carePackItems}
                onChange={onChangeSelection}
                selectedValue={selectedCarepack.sku}
                title={getDerivedTitle(productInitial)}
                disclaimer={disclaimer}
            />
            <CompareCarePacks
                isOpen={isModalOpen}
                carePackItems={carePackItems}
                onChangeSelection={onChangeSelection}
                selectedCarepack={selectedCarepack}
                modalClose={modalClose}
                serviceBundle={isServiceBundle ? serviceBundle : null}
            />
        </div>
    );
};

export default withConfigurations(
    withPageComponents(carepacks, { components: ['pdpCarePackBundle', 'productInitial', 'pdpSettings'], device: true }),
);
